import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { getCollection, addDocument, updateDocument, deleteDocument } from '../../firebase/firestore';
import { uploadFile, deleteFile } from '../../firebase/storage';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import './AdminPages.css';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    detailedDescription: '',
    icon: 'chart',
    image: '',
    mainImage: '',
    pricing: '',
    features: [''],
    sections: [{ title: '', content: '' }],
    slug: '',
    active: true,
    order: 1
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const iconOptions = [
    { value: 'chart', label: 'Chart (Business Strategy)' },
    { value: 'lightbulb', label: 'Lightbulb (Innovation)' },
    { value: 'cogs', label: 'Cogs (Process)' },
    { value: 'users', label: 'Users (Team)' }
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const { db } = await import('../../firebase/config');

      if (!db) {
        setLoading(false);
        return;
      }

      const data = await getCollection('services');
      setServices(data.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('Error fetching services:', error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500000) {
        alert('Image is too large. Please use an image under 500KB or enter an image URL instead.');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSave = { ...formData };

      // Auto-generate slug if not provided
      if (!dataToSave.slug) {
        dataToSave.slug = generateSlug(dataToSave.title);
      }

      // Filter out empty features and sections
      dataToSave.features = dataToSave.features.filter(f => f.trim() !== '');
      dataToSave.sections = dataToSave.sections.filter(s => s.title.trim() !== '' || s.content.trim() !== '');

      // If a new image file was uploaded, try to upload it to Firebase Storage
      if (imageFile) {
        try {
          const timestamp = Date.now();
          const fileName = `services/${timestamp}_${imageFile.name}`;
          const imageUrl = await uploadFile(imageFile, fileName);
          dataToSave.image = imageUrl;

          // If editing and there was an old image, delete it
          if (editingService && editingService.image && editingService.image.includes('firebase')) {
            try {
              const oldImagePath = editingService.image.split('/o/')[1]?.split('?')[0];
              if (oldImagePath) {
                await deleteFile(decodeURIComponent(oldImagePath));
              }
            } catch (err) {
              console.log('Could not delete old image:', err);
            }
          }
        } catch (uploadError) {
          console.error('Storage upload failed:', uploadError);
          alert('Firebase Storage is not configured. Please enter an image URL instead or configure Firebase Storage CORS settings.');
          return;
        }
      }

      if (editingService) {
        await updateDocument('services', editingService.id, dataToSave);
      } else {
        await addDocument('services', dataToSave);
      }
      fetchServices();
      resetForm();
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Error saving service: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        const service = services.find(s => s.id === id);

        // Delete the image from storage if it exists
        if (service && service.image && service.image.includes('firebase')) {
          try {
            const imagePath = service.image.split('/o/')[1]?.split('?')[0];
            if (imagePath) {
              await deleteFile(decodeURIComponent(imagePath));
            }
          } catch (err) {
            console.log('Could not delete image:', err);
          }
        }

        await deleteDocument('services', id);
        fetchServices();
      } catch (error) {
        console.error('Error deleting service:', error);
      }
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      description: service.description,
      detailedDescription: service.detailedDescription || '',
      icon: service.icon || 'chart',
      image: service.image || '',
      mainImage: service.mainImage || '',
      pricing: service.pricing || '',
      features: service.features || [''],
      sections: service.sections || [{ title: '', content: '' }],
      slug: service.slug || '',
      active: service.active,
      order: service.order
    });
    setImagePreview(service.image || '');
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      detailedDescription: '',
      icon: 'chart',
      image: '',
      mainImage: '',
      pricing: '',
      features: [''],
      sections: [{ title: '', content: '' }],
      slug: '',
      active: true,
      order: 1
    });
    setEditingService(null);
    setShowForm(false);
    setImageFile(null);
    setImagePreview('');
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const addFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ''] });
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const addSection = () => {
    setFormData({ ...formData, sections: [...formData.sections, { title: '', content: '' }] });
  };

  const removeSection = (index) => {
    const newSections = formData.sections.filter((_, i) => i !== index);
    setFormData({ ...formData, sections: newSections });
  };

  const updateSection = (index, field, value) => {
    const newSections = [...formData.sections];
    newSections[index][field] = value;
    setFormData({ ...formData, sections: newSections });
  };

  if (loading) return <Loader />;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Services</h1>
          <p>Manage your service offerings</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Add Service'}
        </Button>
      </div>

      {showForm && (
        <motion.div
          className="admin-form-card"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h3>{editingService ? 'Edit Service' : 'Add New Service'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Short Description (for cards)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                placeholder="Brief description shown on service cards"
              />
            </div>
            <div className="form-group">
              <label>Detailed Description (for detail page)</label>
              <textarea
                value={formData.detailedDescription}
                onChange={(e) => setFormData({ ...formData, detailedDescription: e.target.value })}
                rows="6"
                placeholder="Full description shown on service detail page"
              />
            </div>
            <div className="form-group">
              <label>Pricing</label>
              <input
                type="text"
                value={formData.pricing}
                onChange={(e) => setFormData({ ...formData, pricing: e.target.value })}
                placeholder="e.g., $299/month or Contact for pricing"
              />
            </div>
            <div className="form-group">
              <label>URL Slug (auto-generated from title)</label>
              <input
                type="text"
                value={formData.slug || generateSlug(formData.title)}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="service-name"
              />
              <small style={{ color: '#666', fontSize: '0.85rem' }}>
                Used in URL: /services/{formData.slug || generateSlug(formData.title)}
              </small>
            </div>
            <div className="form-group">
              <label>Features</label>
              {formData.features.map((feature, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => updateFeature(index, e.target.value)}
                    placeholder={`Feature ${index + 1}`}
                    style={{ flex: 1 }}
                  />
                  {formData.features.length > 1 && (
                    <button type="button" onClick={() => removeFeature(index)} className="btn-delete" style={{ padding: '0.5rem 1rem' }}>
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addFeature} className="btn-secondary" style={{ marginTop: '0.5rem' }}>
                + Add Feature
              </button>
            </div>
            <div className="form-group">
              <label>Content Sections</label>
              {formData.sections.map((section, index) => (
                <div key={index} style={{ border: '1px solid #333', padding: '1rem', marginBottom: '1rem', borderRadius: '8px' }}>
                  <input
                    type="text"
                    value={section.title}
                    onChange={(e) => updateSection(index, 'title', e.target.value)}
                    placeholder="Section Title"
                    style={{ marginBottom: '0.5rem' }}
                  />
                  <textarea
                    value={section.content}
                    onChange={(e) => updateSection(index, 'content', e.target.value)}
                    placeholder="Section Content"
                    rows="4"
                  />
                  {formData.sections.length > 1 && (
                    <button type="button" onClick={() => removeSection(index)} className="btn-delete" style={{ marginTop: '0.5rem' }}>
                      Remove Section
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addSection} className="btn-secondary">
                + Add Section
              </button>
            </div>
            <div className="form-group">
              <label>Icon</label>
              <select
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              >
                {iconOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Image URL (Optional)</label>
              <input
                type="url"
                placeholder="https://example.com/image.jpg"
                value={formData.image}
                onChange={(e) => {
                  setFormData({ ...formData, image: e.target.value });
                  setImagePreview(e.target.value);
                }}
              />
              <small style={{ color: '#666', fontSize: '0.85rem' }}>
                Enter an image URL (recommended) or upload a file below (max 500KB)
              </small>
            </div>
            <div className="form-group">
              <label>Or Upload Image (max 500KB)</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Main Image URL (for detail page hero)</label>
              <input
                type="url"
                placeholder="https://example.com/hero-image.jpg"
                value={formData.mainImage}
                onChange={(e) => setFormData({ ...formData, mainImage: e.target.value })}
              />
              <small style={{ color: '#666', fontSize: '0.85rem' }}>
                Large image displayed at the top of the service detail page
              </small>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="form-actions">
              <Button type="submit">{editingService ? 'Update' : 'Create'} Service</Button>
              <Button variant="secondary" onClick={resetForm}>Cancel</Button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="admin-page-content">
        {services.length === 0 ? (
          <div className="empty-state-card">
            <p>📝 No services yet. Click "Add Service" to create one.</p>
          </div>
        ) : (
          <div className="admin-grid">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                className="admin-card service-admin-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                {service.image && (
                  <div className="admin-service-image">
                    <img src={service.image} alt={service.title} />
                  </div>
                )}
                <div className="admin-card-content">
                  <div className="admin-card-header">
                    <h3>{service.title}</h3>
                    <span className={`status-badge ${service.active ? 'active' : 'inactive'}`}>
                      {service.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p>{service.description}</p>
                  <div className="admin-card-footer">
                    <span>Order: {service.order}</span>
                    <div className="admin-card-actions">
                      <button onClick={() => handleEdit(service)} className="btn-edit">Edit</button>
                      <button onClick={() => handleDelete(service.id)} className="btn-delete">Delete</button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminServices;
